import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";

const PAYING_ROLES = ["parent", "doctor", "school_admin", "class_teacher"];
const UNLIMITED_ROLES = ["super_admin", "admin"];

/**
 * Wraps tool pages. On mount:
 *  - Admins (super_admin/admin): pass through (unlimited credits).
 *  - Paying roles with balance > 0: pass through.
 *  - Paying roles with balance == 0: redirect to /checkout (with intent to return).
 */
const CreditsGuard = ({ children, tool }) => {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (authLoading || !user) return;
    if (UNLIMITED_ROLES.includes(user.role)) { setChecking(false); return; }
    if (!PAYING_ROLES.includes(user.role)) { setChecking(false); return; }

    let cancelled = false;
    api.get("/api/payments/me")
      .then(({ data }) => {
        if (cancelled) return;
        if ((data.balance || 0) <= 0 && !(tool && (data.tool_credits?.[tool] || 0) > 0)) {
          nav("/checkout", { replace: true, state: { from: loc.pathname, requireCredits: true } });
        } else {
          setChecking(false);
        }
      })
      .catch(() => { if (!cancelled) setChecking(false); });

    return () => { cancelled = true; };
  }, [user, authLoading, nav, loc.pathname, tool]);

  if (authLoading || checking) {
    return (
      <div className="min-h-screen bg-[#0A1029] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#4ECDC4] animate-spin mx-auto mb-3" />
          <p className="text-white/50 text-sm">Checking your report credits…</p>
        </div>
      </div>
    );
  }
  return children;
};

export default CreditsGuard;
